import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Plus, Trash2, Upload, X, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AddProduct = () => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', category: '', shortDescription: '', fullDescription: '',
    pricingType: 'single', price: '', costPrice: '', stock: '',
    sizes: [{ label: '', price: '', stock: '' }],
    badge: '', isActive: true, isFeatured: false, isBestSeller: false,
    images: [],
  });

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(console.error);
    if (isEdit) {
      axios.get(`${API}/products/${id}`, { headers: authHeader }).then(r => {
        const p = r.data;
        setForm({
          name: p.name || '', category: p.category?._id || p.category || '',
          shortDescription: p.shortDescription || '', fullDescription: p.fullDescription || '',
          pricingType: p.pricingType || 'single', price: p.price || '', costPrice: p.costPrice ?? '', stock: p.stock || '',
          sizes: p.sizes?.length ? p.sizes.map(s => ({ label: s.label, price: s.price, stock: s.stock })) : [{ label: '', price: '', stock: '' }],
          badge: p.badge || '', isActive: p.isActive !== false, isFeatured: !!p.isFeatured, isBestSeller: !!p.isBestSeller,
          images: p.images || [],
        });
      }).catch(console.error);
    }
  }, [id]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => {
        setForm(f => ({ ...f, images: [...f.images, ev.target.result].slice(0, 5) }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) { toast.error('Name and category are required'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        costPrice: Number(form.costPrice) || 0,
        stock: Number(form.stock) || 0,
        sizes: form.pricingType === 'sizewise' ? form.sizes.map(s => ({ label: s.label, price: Number(s.price), stock: Number(s.stock) })) : [],
      };
      if (isEdit) {
        await axios.put(`${API}/products/${id}`, payload, { headers: authHeader });
        toast.success('Product updated!');
      } else {
        await axios.post(`${API}/products`, payload, { headers: authHeader });
        toast.success('Product added!');
      }
      navigate('/admin/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setSize = (i, key, val) => setForm(f => ({ ...f, sizes: f.sizes.map((s, idx) => idx === i ? { ...s, [key]: val } : s) }));
  const addSize = () => setForm(f => ({ ...f, sizes: [...f.sizes, { label: '', price: '', stock: '' }] }));
  const removeSize = (i) => setForm(f => ({ ...f, sizes: f.sizes.filter((_, idx) => idx !== i) }));

  const previewImage = form.images?.[0];

  return (
    <div className="p-6 bg-[#FDF8F3] min-h-full" data-testid="admin-add-product">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">{isEdit ? 'Edit Product' : 'Add New Product'}</h1>
          <div className="flex items-center gap-1 text-xs text-[#7A6A63] mt-1">
            <Link to="/admin/dashboard" className="hover:text-[#7A1125]">Dashboard</Link>
            <span>›</span>
            <Link to="/admin/products" className="hover:text-[#7A1125]">Products</Link>
            <span>›</span>
            <span>{isEdit ? 'Edit' : 'Add New'}</span>
          </div>
        </div>
        <Link to="/admin/products" className="flex items-center gap-2 border border-[#ECE0DA] px-4 py-2 rounded-full text-sm hover:bg-white transition-colors" data-testid="back-to-products">
          <ArrowLeft size={14} /> Back
        </Link>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left — Product Info + Images */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5">
              <h3 className="font-playfair font-semibold text-[#7A1125] mb-4 flex items-center gap-2">
                <span className="w-5 h-5 rounded bg-[#7A1125]/10 flex items-center justify-center text-xs">1</span>
                Product Information
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Product Name *</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Enter product name" required
                    className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]" data-testid="product-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Category *</label>
                    <select value={form.category} onChange={e => set('category', e.target.value)} required
                      className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] bg-white" data-testid="product-category-select">
                      <option value="">Select...</option>
                      {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Badge</label>
                    <input value={form.badge} onChange={e => set('badge', e.target.value)} placeholder="Hot, New..."
                      className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]" data-testid="product-badge-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Short Description</label>
                  <textarea value={form.shortDescription} onChange={e => set('shortDescription', e.target.value)} rows={2} maxLength={150}
                    placeholder="Brief product description"
                    className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] resize-none" data-testid="product-short-desc" />
                  <p className="text-xs text-[#7A6A63] text-right">{form.shortDescription.length}/150</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Full Description</label>
                  <textarea value={form.fullDescription} onChange={e => set('fullDescription', e.target.value)} rows={3}
                    placeholder="Detailed description"
                    className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] resize-none" data-testid="product-full-desc" />
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5">
              <h3 className="font-playfair font-semibold text-[#7A1125] mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded bg-[#7A1125]/10 flex items-center justify-center text-xs">2</span>
                Product Images
              </h3>
              <label className="block border-2 border-dashed border-[#ECE0DA] rounded-xl p-6 text-center cursor-pointer hover:border-[#7A1125] transition-colors" data-testid="image-upload-zone">
                <Upload size={24} className="mx-auto text-[#7A6A63] mb-2" />
                <p className="text-sm text-[#7A6A63]"><span className="text-[#7A1125] font-semibold">Click to upload</span> or drag and drop</p>
                <p className="text-xs text-[#7A6A63] mt-1">PNG, JPG, WEBP up to 5MB</p>
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
              </label>
              {form.images.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative w-16 h-16">
                      <img src={img} className="w-full h-full object-cover rounded-lg border border-[#ECE0DA]" alt="" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=60'; }} />
                      <button type="button" onClick={() => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                  {form.images.length < 5 && (
                    <label className="w-16 h-16 border-2 border-dashed border-[#ECE0DA] rounded-lg flex items-center justify-center cursor-pointer hover:border-[#7A1125] transition-colors">
                      <Plus size={18} className="text-[#7A6A63]" />
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Center — Pricing */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5">
              <h3 className="font-playfair font-semibold text-[#7A1125] mb-4 flex items-center gap-2">
                <span className="w-5 h-5 rounded bg-[#7A1125]/10 flex items-center justify-center text-xs">3</span>
                Pricing & Stock
              </h3>

              {/* Cost Price — admin-only, used for profit calculations, never shown to customers */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Cost Price (₹) <span className="text-[#7A1125]">— Admin Only</span></label>
                <input type="number" value={form.costPrice} onChange={e => set('costPrice', e.target.value)} min="0" placeholder="0"
                  className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]" data-testid="product-cost-price-input" />
                <p className="text-[10px] text-[#7A6A63] mt-1">Internal only — never shown to customers or in public product data.</p>
              </div>

              {/* Pricing type toggle */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-2">Pricing Type</label>
                <div className="flex border border-[#ECE0DA] rounded-xl overflow-hidden">
                  {['single', 'sizewise'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => set('pricingType', type)}
                      className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${form.pricingType === type ? 'bg-[#7A1125] text-white' : 'text-[#7A6A63] hover:bg-[#FDF8F3]'}`}
                      data-testid={`pricing-type-${type}`}
                    >
                      {type === 'single' ? 'Single Price' : 'Size Wise Prices'}
                    </button>
                  ))}
                </div>
              </div>

              {form.pricingType === 'single' ? (
                <div className="space-y-3">
                  <p className="text-xs text-[#7A6A63]">Set a single price and stock for this product</p>
                  <div>
                    <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Price (₹)</label>
                    <input type="number" value={form.price} onChange={e => set('price', e.target.value)} min="0" placeholder="0"
                      className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]" data-testid="product-price-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Stock Quantity</label>
                    <input type="number" value={form.stock} onChange={e => set('stock', e.target.value)} min="0" placeholder="0"
                      className="w-full px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]" data-testid="product-stock-input" />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-[#7A6A63]">Add size, price and stock for this product</p>
                  <div className="space-y-2">
                    {form.sizes.map((s, i) => (
                      <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                        <div>
                          {i === 0 && <label className="block text-xs text-[#7A6A63] mb-1">Size</label>}
                          <input value={s.label} onChange={e => setSize(i, 'label', e.target.value)} placeholder="500g"
                            className="w-full px-2 py-2 border border-[#ECE0DA] rounded-lg text-xs focus:outline-none focus:border-[#7A1125]" data-testid={`size-label-${i}`} />
                        </div>
                        <div>
                          {i === 0 && <label className="block text-xs text-[#7A6A63] mb-1">Price (₹)</label>}
                          <input type="number" value={s.price} onChange={e => setSize(i, 'price', e.target.value)} min="0" placeholder="299"
                            className="w-full px-2 py-2 border border-[#ECE0DA] rounded-lg text-xs focus:outline-none focus:border-[#7A1125]" data-testid={`size-price-${i}`} />
                        </div>
                        <div>
                          {i === 0 && <label className="block text-xs text-[#7A6A63] mb-1">Stock</label>}
                          <input type="number" value={s.stock} onChange={e => setSize(i, 'stock', e.target.value)} min="0" placeholder="50"
                            className="w-full px-2 py-2 border border-[#ECE0DA] rounded-lg text-xs focus:outline-none focus:border-[#7A1125]" data-testid={`size-stock-${i}`} />
                        </div>
                        <button type="button" onClick={() => removeSize(i)} className="p-2 text-red-400 hover:text-red-600 transition-colors mb-0">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={addSize}
                      className="w-full py-2 border border-dashed border-[#ECE0DA] rounded-xl text-sm text-[#7A6A63] hover:border-[#7A1125] hover:text-[#7A1125] transition-colors flex items-center justify-center gap-1"
                      data-testid="add-size-button">
                      <Plus size={14} /> Add Size
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right — Options + Preview */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5">
              <h3 className="font-playfair font-semibold text-[#7A1125] mb-4 flex items-center gap-2">
                <span className="w-5 h-5 rounded bg-[#7A1125]/10 flex items-center justify-center text-xs">4</span>
                Additional Options
              </h3>
              <div className="space-y-3">
                {[
                  { key: 'isActive', label: 'Active Product', sub: 'Product will be visible to customers' },
                  { key: 'isFeatured', label: 'Featured Product', sub: 'Show this product on homepage' },
                  { key: 'isBestSeller', label: 'Best Seller', sub: 'Mark this product as best seller' },
                ].map(opt => (
                  <label key={opt.key} className="flex items-start gap-3 cursor-pointer" data-testid={`toggle-${opt.key}`}>
                    <input type="checkbox" checked={form[opt.key]} onChange={e => set(opt.key, e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-[#ECE0DA] text-[#7A1125] focus:ring-[#7A1125]/20 cursor-pointer" />
                    <div>
                      <p className="text-sm font-medium text-[#1F1410]">{opt.label}</p>
                      <p className="text-xs text-[#7A6A63]">{opt.sub}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5">
              <h3 className="font-playfair font-semibold text-[#7A1125] mb-4 flex items-center gap-2">
                <span className="w-5 h-5 rounded bg-[#7A1125]/10 flex items-center justify-center text-xs">5</span>
                Product Preview
              </h3>
              <div className="border border-[#ECE0DA] rounded-xl overflow-hidden">
                {previewImage ? (
                  <img src={previewImage} alt="Preview" className="w-full h-36 object-cover" onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="w-full h-36 bg-[#FDF8F3] flex items-center justify-center">
                    <p className="text-[#7A6A63] text-xs text-center px-4">Preview will appear here<br/>Add product details and images</p>
                  </div>
                )}
                {form.name && (
                  <div className="p-3">
                    <p className="font-playfair font-semibold text-[#1F1410] text-sm">{form.name}</p>
                    {form.shortDescription && <p className="text-xs text-[#7A6A63] mt-0.5 line-clamp-2">{form.shortDescription}</p>}
                    <p className="text-[#7A1125] font-bold mt-1">
                      {form.pricingType === 'sizewise' && form.sizes[0]?.price ? `From ₹${form.sizes[0].price}` : form.price ? `₹${form.price}` : '₹—'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button type="button" onClick={() => setForm(f => ({ ...f, name: '', shortDescription: '', price: '', stock: '', images: [] }))}
                className="flex-1 py-3 border border-[#ECE0DA] rounded-full text-sm font-semibold text-[#7A6A63] hover:bg-white transition-colors" data-testid="reset-product-form">
                Reset
              </button>
              <button type="submit" disabled={loading}
                className="flex-1 py-3 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold rounded-full transition-colors" data-testid="save-product-button">
                {loading ? 'Saving...' : isEdit ? 'Update Product' : 'Save Product'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddProduct;
